import { byCreatedAt, type OutboxStore } from './store';
import type { OutboxItem } from './types';

const ITEMS = 'items';
const VERSION = 1;

function promised<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/** Resolves when the transaction commits, not when its request succeeds: only then is the write durable. */
function committed(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error ?? new Error('Outbox transaction aborted'));
  });
}

function openDb(factory: IDBFactory, name: string): Promise<IDBDatabase> {
  const request = factory.open(name, VERSION);
  request.onupgradeneeded = () => {
    const db = request.result;
    if (!db.objectStoreNames.contains(ITEMS)) {
      db.createObjectStore(ITEMS, { keyPath: 'id' }).createIndex('userId', 'userId');
    }
  };
  return promised(request);
}

/** The web's outbox store. Items are structured-cloned by IndexedDB, so reads are copies already. */
export async function createIdbStore(options: { name?: string; factory?: IDBFactory } = {}): Promise<OutboxStore> {
  const db = await openDb(options.factory ?? indexedDB, options.name ?? 'scopie-outbox');

  const write = async (fn: (items: IDBObjectStore) => void) => {
    const tx = db.transaction(ITEMS, 'readwrite');
    const done = committed(tx);
    fn(tx.objectStore(ITEMS));
    await done;
  };

  return {
    put: (item) => write((items) => items.put(item)),
    async get(id) {
      const item = await promised(db.transaction(ITEMS).objectStore(ITEMS).get(id));
      return (item as OutboxItem | undefined) ?? undefined;
    },
    async list(filter) {
      const items = db.transaction(ITEMS).objectStore(ITEMS);
      const all =
        filter?.userId === undefined ? items.getAll() : items.index('userId').getAll(filter.userId);
      return ((await promised(all)) as OutboxItem[]).sort(byCreatedAt);
    },
    remove: (id) => write((items) => items.delete(id)),
    async ids() {
      return (await promised(db.transaction(ITEMS).objectStore(ITEMS).getAllKeys())).map(String);
    },
  };
}
